import React, { useState, useRef, useEffect } from 'react';
import { HexColorPicker } from 'react-colorful';
import { Paintbrush } from 'lucide-react';
import './ColorPicker.css';

interface ColorPickerProps {
  color: string;
  onChange: (color: string) => void;
  label?: string;
}

export const ColorPicker: React.FC<ColorPickerProps> = ({ color, onChange, label }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState(color);
  const popoverRef = useRef<HTMLDivElement>(null);

  const [prevColor, setPrevColor] = useState(color);
  if (color !== prevColor) {
    setPrevColor(color);
    setInputValue(color);
  }

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target as Node)) {
        setIsOpen(false);
        // On close, if input is invalid, it already reverted on blur.
      }
    };
    if (isOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => { document.removeEventListener('mousedown', handleClickOutside); };
  }, [isOpen]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
  };

  const handleInputBlur = () => {
    if (/^#[0-9A-F]{6}$/i.test(inputValue)) {
      onChange(inputValue);
    } else {
      setInputValue(color); // Revert
    }
  };

  return (
    <div className="color-picker-container">
      {label && <label className="color-picker-label">{label}</label>}
      <div className="color-picker-trigger">
        <button
          className="color-swatch"
          style={{ backgroundColor: color }}
          onClick={() => { setIsOpen(true); }}
          aria-label="Pick color"
        >
          <span className="color-swatch-edit-icon"><Paintbrush size={14} /></span>
        </button>
        <span className="color-value">{color}</span>
      </div>

      {isOpen && (
        <div className="color-picker-popover" ref={popoverRef}>
          <HexColorPicker color={color} onChange={onChange} />
          <input
            className="hex-input"
            type="text"
            value={inputValue}
            onChange={handleInputChange}
            onBlur={handleInputBlur}
            onKeyDown={(e) => { if (e.key === 'Enter') handleInputBlur(); }}
          />
        </div>
      )}
    </div>
  );
};
